import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { NGXLogger as LoggerService } from 'ngx-logger';

import { EmbedModeService } from './embed-mode.service';
import { UIMesageService } from './ui-message.service';
import { TranslationGeneralService } from '../translation/translation.general.service';
import {
    consumeLanguageSwitchFlag,
    markContentLangHintShown,
    wasContentLangHintShown,
} from './lang-pref';

/**
 * Chrome-≠-Inhalt-Hinweis (Konzept Prinzip 6): Poll-Inhalte sind in der
 * Ersteller-Sprache materialisiert und bleiben es. Wechselt ein Betrachter auf
 * einer Vote-Seite die Sprache, sagt die Snackbar das einmalig dazu, statt die
 * Seite „halb uebersetzt" aussehen zu lassen.
 *
 * Mechanik: jeder sprachwechselnde Redirect setzt `whocan.justSwitched`
 * (sessionStorage, siehe `lang-pref.ts` beider Systeme). Nur vote*-Routen lesen
 * und loeschen den Flag; die Snackbar erscheint genau einmal pro Browser
 * (`whocan.contentLangHintShown`) und nie im Embed-Mode.
 */
@Injectable({ providedIn: 'root' })
export class ContentLanguageHintService {
    constructor(
        private router: Router,
        private embedMode: EmbedModeService,
        private uiMessage: UIMesageService,
        private trans: TranslationGeneralService,
        private logger: LoggerService,
    ) { }

    public subscribe(): void {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => void this.maybeShowHint(event.urlAfterRedirects));
    }

    private async maybeShowHint(url: string): Promise<void> {
        // Die vier Vote-Routen: vote, vote-preview, vote-response,
        // vote-preview-response — englische Funktions-Slugs in allen Sprachen.
        if (!url.startsWith('/vote')) {
            return;
        }
        if (this.embedMode.isEmbedded) {
            return;
        }
        if (!consumeLanguageSwitchFlag()) {
            return;
        }
        if (wasContentLangHintShown()) {
            return;
        }
        markContentLangHintShown();
        // Sicherstellen, dass das general-Dictionary geladen ist (no-op falls schon).
        await this.trans.loadTransLationFiles();
        const hint = this.trans.translate('language.contentHint');
        this.logger.log('ContentLanguageHint', hint);
        this.uiMessage.info(hint);
    }
}
